import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getDatabricksJobAutoRecommendations, toggleDatabricksJobAutoTune } from "@/helpers/apis";
import { AUTO_TUNE_HISTORY_KEY, AUTO_TUNE_STATUS_KEY } from "@/pages/jobs/auto-tune/hooks";
import type { ToggleAutoTuneResponse } from "../types";

export const AUTO_RECOMMENDATIONS_KEY = "jobs-auto-recommendations";

// Shared mutation key so any component on the page can observe whether a
// toggle is in flight via ``useIsMutating`` — used by the DAG container to
// dim every task card during a master cascade so the user sees something
// happening even before the BE responds.
export const TOGGLE_AUTO_TUNE_MUTATION_KEY = "toggle-job-auto-tune";

export const toggleAutoTuneMutationKey = (
  jobId: string,
  instanceId: number | string | undefined,
  workspaceId?: string
) => [TOGGLE_AUTO_TUNE_MUTATION_KEY, jobId, instanceId, workspaceId] as const;

export const autoRecommendationsKey = (
  jobId: string,
  instanceId: number | string | undefined,
  workspaceId?: string
) => [AUTO_RECOMMENDATIONS_KEY, jobId, instanceId, workspaceId] as const;

/**
 * Read recommendations for a job. The response carries `auto_tune_mode`
 * (LEFT JOIN'd from the configuration table on the BE) so the toggle
 * position is always consistent with what the worker sees — no client
 * polling, no optimistic state.
 *
 * ``workspaceId`` (sourced from the job-detail response, alongside
 * ``instanceId``) is the second half of the AT scope key — the BE
 * scopes config + audit reads/writes by ``(workspace_id, instance_id,
 * entity_id)`` so cross-workspace ``job_id`` collisions don't bleed.
 */
export const useJobAutoRecommendations = ({
  jobId,
  instanceId,
  workspaceId,
  enabled = true,
}: {
  jobId: string;
  instanceId?: number | string;
  workspaceId?: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: autoRecommendationsKey(jobId, instanceId, workspaceId),
    queryFn: () =>
      getDatabricksJobAutoRecommendations(jobId, {
        instance_id: instanceId ?? "",
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
      }),
    enabled: enabled && Boolean(jobId) && instanceId !== undefined && instanceId !== "",
  });
};

/**
 * Toggle auto-tune on/off for a recommendation. The mutation invalidates
 * the read query on success so the card re-renders from server truth —
 * no optimistic flip. On error the toast surfaces the failure and the
 * toggle position naturally reverts because the cache is unchanged.
 *
 * TEMP DEBUG LOGS (POC validation): the mutation logs request +
 * response payloads to the console under `[autotune]`. Remove the
 * console calls once we're past the team demo.
 */
export const useToggleJobAutoTune = ({
  jobId,
  instanceId,
  workspaceId,
}: {
  jobId: string;
  instanceId: number | string;
  workspaceId?: string;
}) => {
  const queryClient = useQueryClient();
  const queryKey = autoRecommendationsKey(jobId, instanceId, workspaceId);

  return useMutation<
    ToggleAutoTuneResponse | undefined,
    Error,
    {
      enabled: boolean;
      taskKey?: string;
      force?: boolean;
      scope?: "all_tasks" | "specific_tasks";
      enabledTaskKeys?: string[];
    }
  >({
    mutationKey: toggleAutoTuneMutationKey(jobId, instanceId, workspaceId),
    mutationFn: async ({ enabled, taskKey, force, scope, enabledTaskKeys }) => {
      const response = await toggleDatabricksJobAutoTune(
        jobId,
        { instance_id: instanceId, workspace_id: workspaceId, task_key: taskKey, force },
        {
          enabled,
          ...(scope !== undefined ? { scope } : {}),
          ...(enabledTaskKeys !== undefined ? { enabled_task_keys: enabledTaskKeys } : {}),
        }
      );
      return response;
    },
    // Await the refetch so the mutation settles only after the cache
    // holds fresh data. Without this the master switch flickers from
    // enabled → loading → enabled (stale) → disabled.
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey }),
        queryClient.invalidateQueries({ queryKey: [AUTO_TUNE_STATUS_KEY, jobId] }),
        queryClient.invalidateQueries({ queryKey: [AUTO_TUNE_HISTORY_KEY, jobId] }),
      ]);
    },
  });
};
